import { FunctionComponent, useCallback, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Container,
    Drawer,
    Stack,
    SvgIcon,
    Typography,
    useTheme,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@heroicons/react/24/solid/PencilIcon';
import ImpersonateIcon from '@heroicons/react/24/solid/ArrowTopRightOnSquareIcon';
import BugAntIcon from '@heroicons/react/24/solid/BugAntIcon';
import { debounce } from 'lodash';
import { useGetAllLiftOperatorsQuery } from '@/api/v2/liftOperator';
import {
    CreateLiftOperator,
    createNewLiftOperator,
    LiftOperator,
} from '@/models/liftoperator';
import { StarjackTable } from '@/components/Table/v2/Table';
import { LiftOperatorDrawerContent } from '@/pageContents/liftOperatorsContent/LiftOperatorDrawerContent';
import { FormErrorContextProvider } from '@/context/FormErrorContextProvider';
import { LiftOperatorSearch } from '@/pageContents/liftOperatorsContent/LiftOperatorSearch';
import { usePostImpersonateMutation } from '@/api/v2/impersonate';
import { useNotification } from '@starjack/features/notifications';
import CircularProgress from '@mui/material/CircularProgress';
import { useSearchParams } from 'react-router-dom';
import { useCsvConfig } from '@/hooks/useCsvConfig';
import { usePagination } from '@starjack/shared-components';

type LiftOperatorFilter = {
    query: string;
    showInactive: boolean;
    showSmartpricer: boolean;
    showIncert: boolean;
    showDynamicPricing: boolean;
};

export const LiftOperatorPageContent: FunctionComponent = () => {
    const theme = useTheme();
    const { data: liftOperators = [], isFetching } =
        useGetAllLiftOperatorsQuery();
    const [drawerConfig, setDrawerConfig] = useState<{
        isOpen: boolean;
        liftOperator: LiftOperator | CreateLiftOperator;
    }>({ isOpen: false, liftOperator: null });
    const [filter, setFilter] = useState<LiftOperatorFilter>({
        query: '',
        showInactive: false,
        showSmartpricer: false,
        showIncert: false,
        showDynamicPricing: false,
    });
    const { onGenerateCSVWithDownload } = useCsvConfig({
        useKeysAsHeaders: true,
        filename: 'Bergbahnen',
        fileExtension: 'csv',
    });
    const displayItems = useFilterDisplayItems({
        liftOperators,
        filter,
    });

    const {
        currentItems,
        setNextPage,
        page,
        itemsPerPage,
        setItemsPerPage,
        countTotal,
    } = usePagination({
        items: displayItems,
        itemsPerPage: 10,
    });

    const [postImpersonate] = usePostImpersonateMutation();
    const { addNotification } = useNotification();
    const [rowLoading, setRowLoading] = useState<string>(null);
    const [searchParams] = useSearchParams();

    const onFilterChange = useCallback(
        debounce((value) => {
            setFilter((prevState) => ({
                ...prevState,
                query: value,
            }));
        }, 250),
        []
    );

    const onCloseDrawer = () => {
        setDrawerConfig((prevState) => ({
            ...prevState,
            isOpen: false,
        }));
    };

    const getImpersonateData = (liftOperator: LiftOperator) => {
        setRowLoading(liftOperator.id);
        postImpersonate({
            liftOperatorId: liftOperator.id,
        })
            .unwrap()
            .then((impersonateResponse) => {
                setRowLoading(null);

                const targetToSelf = searchParams.get('target') === '_self';
                const url = getAdminBaseUrl(searchParams.get('impersonateTo'));

                window.open(
                    `${url}?jwt=${impersonateResponse.jwt}&validFor=${impersonateResponse.validFor}&refreshCode=${impersonateResponse.refreshCode}&liftOperatorName=${liftOperator.name.de}`,
                    targetToSelf ? '_self' : '_blank'
                );
            })
            .catch(() => {
                addNotification({
                    type: 'error',
                    message: 'Fehler beim Übernehmen der Session',
                });
            });
    };
    return (
        <FormErrorContextProvider>
            <Container
                sx={{
                    mt: 3,
                    ml: 'unset',
                }}
                maxWidth={false}
            >
                <Typography
                    sx={{
                        mb: 8,
                    }}
                    variant='h4'
                >
                    Bergbahnen
                </Typography>
                <Stack spacing={theme.page.verticalSpacing}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                        }}
                    >
                        <Button
                            variant='contained'
                            onClick={() => {
                                setDrawerConfig({
                                    isOpen: true,
                                    liftOperator: createNewLiftOperator(),
                                });
                            }}
                        >
                            + Neue Bergbahn anlegen
                        </Button>
                    </Box>

                    <LiftOperatorSearch
                        onSearchChange={onFilterChange}
                        onShowInActiveChange={(checked) => {
                            setFilter((prevState) => ({
                                ...prevState,
                                showInactive: checked,
                            }));
                        }}
                        onDownloadAsCsv={() => {
                            onGenerateCSVWithDownload(displayItems);
                        }}
                        onChipClick={(chipName) => {
                            switch (chipName) {
                                case 'smartpricer':
                                    setFilter((prevState) => ({
                                        ...prevState,
                                        showSmartpricer:
                                            !prevState.showSmartpricer,
                                    }));
                                    break;
                                case 'incert':
                                    setFilter((prevState) => ({
                                        ...prevState,
                                        showIncert: !prevState.showIncert,
                                    }));
                                    break;
                                case 'dynamicPricing':
                                    setFilter((prevState) => ({
                                        ...prevState,
                                        showDynamicPricing:
                                            !prevState.showDynamicPricing,
                                    }));
                                    break;
                                default:
                                    break;
                            }
                        }}
                    />

                    <StarjackTable<LiftOperator>
                        itemId='id'
                        count={countTotal}
                        items={currentItems}
                        currentPage={page}
                        rowsPerPage={itemsPerPage}
                        onPageChange={setNextPage}
                        onRowsPerPageChange={setItemsPerPage}
                        loading={isFetching}
                        columns={[
                            {
                                label: 'Name',
                                itemTransform: (liftOperator) =>
                                    liftOperator.name.de,
                            },
                            {
                                label: 'Straße',
                                itemTransform: (liftOperator) =>
                                    `${liftOperator.address.street} `,
                            },
                            {
                                label: 'PLZ',
                                itemTransform: (liftOperator) =>
                                    liftOperator.address.zipcode,
                            },
                            {
                                label: 'Stadt',
                                itemTransform: (liftOperator) =>
                                    liftOperator.address.city,
                            },
                            {
                                label: 'E-Mail',
                                itemTransform: (liftOperator) =>
                                    liftOperator.email,
                            },

                            {
                                label: 'Webseite',
                                itemTransform: (liftOperator) =>
                                    liftOperator.homepage,
                            },

                            {
                                label: 'Telefon',
                                itemTransform: (liftOperator) =>
                                    liftOperator.phone,
                            },
                            {
                                label: 'Aktionen',
                                itemTransform: (liftOperator) => (
                                    <Stack
                                        spacing={3}
                                        direction='row'
                                        alignItems='center'
                                    >
                                        <IconButton
                                            onClick={() => {
                                                setDrawerConfig({
                                                    isOpen: true,
                                                    liftOperator,
                                                });
                                            }}
                                        >
                                            <SvgIcon>
                                                <EditIcon />
                                            </SvgIcon>
                                        </IconButton>

                                        {rowLoading === liftOperator.id ? (
                                            <CircularProgress
                                                sx={{
                                                    width: '24px !important',
                                                    height: '24px !important',
                                                }}
                                            />
                                        ) : (
                                            <IconButton
                                                onClick={() => {
                                                    getImpersonateData(
                                                        liftOperator
                                                    );
                                                }}
                                            >
                                                <SvgIcon>
                                                    <ImpersonateIcon />
                                                </SvgIcon>
                                            </IconButton>
                                        )}

                                        <IconButton
                                            component='a'
                                            href={`https://api.starjack.com/bei/v1/admin/catalog-debug/${liftOperator.id}`}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                        >
                                            <SvgIcon>
                                                <BugAntIcon />
                                            </SvgIcon>
                                        </IconButton>
                                    </Stack>
                                ),
                            },
                        ]}
                    />
                </Stack>
                <Drawer
                    anchor='right'
                    open={drawerConfig.isOpen}
                    onClose={onCloseDrawer}
                >
                    <LiftOperatorDrawerContent
                        onClose={onCloseDrawer}
                        liftOperator={drawerConfig.liftOperator}
                    />
                </Drawer>
            </Container>
        </FormErrorContextProvider>
    );
};

const getAdminBaseUrl = (impersonateTo: string | null): string => {
    const ADMIN_LOCAL_URL = 'http://localhost:3001';
    const ADMIN_STAGING_URL = 'https://admin.staging.starjack.com';

    if (impersonateTo === 'local') {
        return ADMIN_LOCAL_URL;
    }
    if (impersonateTo === 'staging') {
        return ADMIN_STAGING_URL;
    }

    const { protocol, hostname, port } = window.location;

    if (/backoffice2?/.test(hostname)) {
        const adminHost = hostname.replace(/backoffice2?/, 'admin');
        return `${protocol}//${adminHost}${port ? `:${port}` : ''}`;
    }

    // Local development default: there is no backoffice host to derive from.
    return ADMIN_LOCAL_URL;
};

const useFilterDisplayItems = ({
    liftOperators,
    filter,
}: {
    liftOperators: LiftOperator[];
    filter: LiftOperatorFilter;
}): LiftOperator[] => {
    return useMemo(() => {
        if (!liftOperators.length) {
            return [];
        }
        const { query, showInactive } = filter;

        let displayItems: LiftOperator[] = liftOperators;

        if (query.length) {
            displayItems = displayItems.filter((liftOperator) =>
                matchName(liftOperator, query)
            );
        }

        if (!showInactive) {
            displayItems = displayItems.filter(
                (liftOperator) => liftOperator.active
            );
        }

        if (filter.showSmartpricer) {
            displayItems = displayItems.filter(
                (liftOperator) => !!liftOperator.smartPricer
            );
        }

        if (filter.showSmartpricer) {
            displayItems = displayItems.filter(
                (liftOperator) => !!liftOperator.smartPricer
            );
        }

        if (filter.showIncert) {
            displayItems = displayItems.filter(
                (liftOperator) => !!liftOperator.incertActive
            );
        }

        if (filter.showDynamicPricing) {
            displayItems = displayItems.filter(
                (liftOperator) => !!liftOperator.dynamicPricingActive
            );
        }

        return displayItems;
    }, [liftOperators, filter]);
};

const matchName = (liftOperator: LiftOperator, searchTerm: string): boolean => {
    return (
        liftOperator.name.de
            .toLocaleLowerCase()
            .includes(searchTerm.toLocaleLowerCase()) ||
        liftOperator.name.en
            ?.toLocaleLowerCase()
            .includes(searchTerm.toLocaleLowerCase()) ||
        liftOperator.id.includes(searchTerm)
    );
};
